import { callSingle, callList, callAction } from '@/lib/rpc/facade';
import type { ListResult, SingleResult, ActionResult } from '@/lib/rpc/types';
import {
  AdminTransaction,
  AdminPayout,
  AdminExternalAccount,
  AdminAuditLog,
  AdminCustomerIdentity
} from '@/types/admin';

export type BffResponse<T> = {
  data: T[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
};

export type RpcResult<T> = 
  | { ok: true; data: T; code?: string; message?: string }
  | { ok: false; code: string; message: string; retryAfterSec?: number };

export type RpcListResult<T> = 
  | { ok: true; data: T[]; total: number; limit?: number; offset?: number; has_more?: boolean; code?: string; message?: string }
  | { ok: false; code: string; message: string; retryAfterSec?: number };

/** 
 * Unified RPC caller for single results - direct replacement for supabase.rpc() 
 * Returns legacy { data, error } format for compatibility 
 */
export async function callRpc<T = any>(
  functionName: string,
  params: Record<string, any> = {}
): Promise<{ data: T | null; error: any | null }> {
  try {
    const result = await callSingle<T>(functionName, params as any, { 
      logLabel: `callRpc(${functionName})` 
    });

    if (!result.ok) {
      const error = result.disabled 
        ? new Error('Service temporarily disabled')
        : (result as any).error; // Type assertion for error case
      return { data: null, error };
    }

    return { data: result.data, error: null };
  } catch (error: any) {
    console.error(`[callRpc] Exception in ${functionName}:`, error);
    return { data: null, error };
  }
}

/** 
 * Unified RPC caller for list results - wrapper around callList
 * Returns legacy BffResponse format for compatibility 
 */
export async function callRpcList<T>(
  functionName: string,
  params: Record<string, any> = {}
): Promise<BffResponse<T>> {
  const result = await callList<T>(functionName, params as any, {
    logLabel: `callRpcList(${functionName})`
  });
  
  if (!result.ok) {
    console.error(`RPC ${functionName} failed:`, (result as any).error);
    throw (result as any).error;
  }

  return {
    data: result.data,
    total: result.total,
    limit: result.limit || 50,
    offset: result.offset || 0,
    has_more: result.has_more || false
  };
}

/** 
 * Modern RPC caller with typed error handling 
 */
export async function callRpcSafe<TOut = any, TIn = any>(
  fn: string, 
  args?: TIn, 
  opts: { timeoutMs?: number; retries?: number } = {}
): Promise<RpcResult<TOut>> {
  try {
    const result = await callSingle<TOut>(fn, args as any, { 
      logLabel: `callRpcSafe(${fn})` 
    });

    if (!result.ok) {
      return mapErrorToRpcResult(result);
    }

    return { ok: true, data: result.data };
  } catch (error: any) {
    console.error(`[callRpcSafe] Exception in ${fn}:`, error);
    return mapExceptionToRpcResult(error);
  }
}

/** 
 * Modern RPC list caller with typed error handling 
 */
export async function callRpcListSafe<TOut = any, TIn = any>(
  fn: string, 
  args?: TIn, 
  opts: { timeoutMs?: number; retries?: number } = {}
): Promise<RpcListResult<TOut>> {
  try {
    const result = await callList<TOut>(fn, args as any, { 
      logLabel: `callRpcListSafe(${fn})` 
    });

    if (!result.ok) {
      return mapErrorToRpcListResult(result);
    }

    return { 
      ok: true, 
      data: result.data, 
      total: result.total,
      limit: result.limit,
      offset: result.offset,
      has_more: result.has_more
    };
  } catch (error: any) {
    console.error(`[callRpcListSafe] Exception in ${fn}:`, error);
    return mapExceptionToRpcListResult(error);
  }
}

/** Map facade error to RpcResult */
function mapErrorToRpcResult(result: SingleResult<any>): RpcResult<never> {
  let code = 'rpc_error';
  let message = 'Service temporarily unavailable';
  let retryAfterSec: number | undefined;

  if (!result.ok) {
    const error = (result as any).error as any;
    if (error?.code) {
      switch (error.code) {
        case 'PGRST116': // Function not found
          code = 'function_not_found';
          message = 'The requested operation is not available';
          break;
        case '42501': // Insufficient privilege
          code = 'access_denied';
          message = 'You do not have permission to perform this action';
          break;
        case '23505': // Unique violation
          code = 'conflict';
          message = 'This operation conflicts with existing data';
          break;
        case '22023': // Invalid parameter value
          code = 'invalid_input';
          message = 'The provided data is invalid';
          break;
        default:
          if (error.message?.includes('rate limit')) {
            code = 'rate_limited';
            message = 'Too many requests. Please try again later.';
            retryAfterSec = 60;
          } else if (error.message?.includes('timeout')) {
            code = 'timeout';
            message = 'Operation timed out. Please try again.';
            retryAfterSec = 5;
          }
      }
    }

    if (result.disabled) {
      code = 'service_disabled';
      message = 'This service is temporarily disabled';
    }
  }

  return { ok: false, code, message, retryAfterSec };
}

/** Map facade error to RpcListResult */
function mapErrorToRpcListResult(result: ListResult<any>): RpcListResult<never> {
  let code = 'rpc_error';
  let message = 'Service temporarily unavailable';
  let retryAfterSec: number | undefined;

  if (!result.ok) {
    const error = (result as any).error as any;
    if (error?.code) {
      switch (error.code) {
        case 'PGRST116': // Function not found
          code = 'function_not_found';
          message = 'The requested operation is not available';
          break;
        case '42501': // Insufficient privilege
          code = 'access_denied';
          message = 'You do not have permission to perform this action';
          break;
        case '23505': // Unique violation
          code = 'conflict';
          message = 'This operation conflicts with existing data';
          break;
        case '22023': // Invalid parameter value
          code = 'invalid_input';
          message = 'The provided data is invalid';
          break;
        default:
          if (error.message?.includes('rate limit')) {
            code = 'rate_limited';
            message = 'Too many requests. Please try again later.';
            retryAfterSec = 60;
          } else if (error.message?.includes('timeout')) {
            code = 'timeout';
            message = 'Operation timed out. Please try again.';
            retryAfterSec = 5;
          }
      }
    }

    if (result.disabled) {
      code = 'service_disabled';
      message = 'This service is temporarily disabled';
    }
  }

  return { ok: false, code, message, retryAfterSec };
}

/** Map exception to RpcResult */
function mapExceptionToRpcResult(error: any): RpcResult<never> {
  if (error?.message?.includes('Failed to fetch')) {
    return {
      ok: false,
      code: 'network_error',
      message: 'Network connection failed. Please check your internet connection.',
      retryAfterSec: 10
    };
  }

  return {
    ok: false,
    code: 'unexpected_error',
    message: 'An unexpected error occurred. Please try again.',
    retryAfterSec: 5
  };
}

/** Map exception to RpcListResult */
function mapExceptionToRpcListResult(error: any): RpcListResult<never> {
  return mapExceptionToRpcResult(error) as RpcListResult<never>;
}

// Admin RPC namespace with typed wrappers using safe methods
export const adminRpc = {
  listTransactions: (params: {
    p_limit: number;
    p_offset: number;
    p_user_id?: string | null;
    p_customer_id?: string | null;
    p_status?: string | null;
    p_sort?: string;
    p_date_from?: string | null;
    p_date_to?: string | null;
    p_search?: string | null;
    p_bridge_customer_id?: string | null;
  }) => callRpcList<AdminTransaction>('admin_list_transactions', params),
  
  listPayouts: (params: {
    p_limit: number;
    p_offset: number;
    p_customer_id?: string | null;
    p_status?: string | null;
    p_sort?: string;
    p_date_from?: string | null;
    p_date_to?: string | null;
    p_search?: string | null;
    p_bridge_customer_id?: string | null;
  }) => callRpcList<AdminPayout>('admin_list_payouts', params),
  
  listAuditLogs: (params: {
    p_limit: number;
    p_offset: number;
    p_actor_id?: string | null;
    p_action?: string | null;
    p_since?: string | null;
  }) => callRpcList<AdminAuditLog>('admin_list_audit_logs', params),
  
  listExternalAccounts: (params: {
    p_limit: number;
    p_offset: number;
    p_customer_id?: string | null;
    p_provider?: string | null;
  }) => callRpcList<AdminExternalAccount>('admin_list_external_accounts', params),
  
  listCustomerIdentities: (params: {
    p_limit: number;
    p_offset: number;
    p_customer_id: string;
    p_state?: string | null;
  }) => callRpcList<AdminCustomerIdentity>('admin_list_customer_identities', params),
};

// User RPC namespace with typed wrappers
export const userRpc = {
  listTransactions: (params: {
    p_limit: number;
    p_offset: number;
    p_status?: string | null;
    p_sort?: string;
  }) => callRpcList('user_list_transactions', params),
  
  listPayouts: (params: {
    p_limit: number;
    p_offset: number;
    p_status?: string | null;
  }) => callRpcList('user_list_payouts', params),
  
  getCustomerIdentities: (params: {
    p_limit: number;
    p_offset: number;
    p_state?: string | null;
  }) => callRpcList('user_get_customer_identities', params),
};